import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { DiscriminatedTreatmentRequestCreatedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface TreatmentRequestCreatedEventProps {
  event: DiscriminatedTreatmentRequestCreatedEvent;
  matterId: string;
}

export const TreatmentRequestCreatedEvent: React.FC<
  TreatmentRequestCreatedEventProps
> = ({ event, matterId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { payload, occurred_at, user, agent } = event;
  const { treatment_request } = payload;

  // Format status
  const status = treatment_request.status
    ? formatStatus(treatment_request.status)
    : 'Unknown';
  const statusVariant = treatment_request.status
    ? getStatusVariant(treatment_request.status)
    : 'outline';

  // Format actor
  const actor = treatment_request.actor
    ? formatActor(treatment_request.actor)
    : null;

  // Check if we have additional details to show
  const hasChannel = !!treatment_request.channel;
  const hasMetadata = !!treatment_request.metadata;
  const hasFaxStatus = !!treatment_request.fax_transmission_status;
  const hasAdditionalDetails = hasChannel || hasMetadata || hasFaxStatus;

  return (
    <EventContainer>
      <EventHeader
        icon={<FileText size={20} />}
        title="Treatment Request Created"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* All key identifiers on one line */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Badge variant={statusVariant}>{status}</Badge>
            {actor && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{actor}</Badge>
              </>
            )}
            {treatment_request.provided_records &&
              treatment_request.provided_records.length > 0 && (
                <>
                  <span className="text-slate-400">•</span>
                  <span className="text-[11px] text-slate-600">
                    {treatment_request.provided_records.join(', ')}
                  </span>
                </>
              )}
            {treatment_request.is_certified && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="default">Certified</Badge>
              </>
            )}
            {treatment_request.is_urgent && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="destructive">Urgent</Badge>
              </>
            )}
          </div>

          {/* Collapsible Additional Details */}
          {hasAdditionalDetails && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-1.5">
                {isOpen ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span>{isOpen ? 'Hide' : 'Show'} additional details</span>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-2 space-y-1.5 border-l-2 border-slate-200 pl-2.5">
                {/* Channel */}
                {hasChannel && (
                  <div className="text-xs">
                    <span className="text-slate-600">Channel:</span>{' '}
                    <Badge variant="outline">
                      {formatChannelType(treatment_request.channel)}
                    </Badge>
                  </div>
                )}

                {/* Fax Status */}
                {hasFaxStatus && treatment_request.fax_transmission_status && (
                  <div className="text-xs">
                    <span className="text-slate-600">Fax Status:</span>{' '}
                    <span className="text-[11px]">
                      {treatment_request.fax_transmission_status.status}
                    </span>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment?request_id=${treatment_request.id}&provider_id=${treatment_request.provider_id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Treatment Request
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatStatus(status: string): string {
  const statuses: Record<string, string> = {
    not_ready: 'Not Ready',
    pending: 'Pending',
    request_submitted: 'Request Submitted',
    request_received: 'Request Received',
    awaiting_invoice: 'Awaiting Invoice',
    pending_payment: 'Pending Payment',
    records_pending: 'Records Pending',
    records_delivered: 'Records Delivered',
    records_ingested: 'Records Ingested',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'records_delivered':
    case 'records_ingested':
      return 'default'; // Green
    case 'pending':
    case 'request_submitted':
    case 'request_received':
    case 'records_pending':
      return 'secondary'; // Blue
    case 'rejected':
    case 'cancelled':
      return 'destructive'; // Red
    case 'not_ready':
    case 'awaiting_invoice':
    case 'pending_payment':
      return 'outline'; // Gray
    default:
      return 'outline';
  }
}

function formatActor(actor: string): string {
  const actors: Record<string, string> = {
    user: 'User',
    agent: 'Agent',
  };
  return actors[actor] || actor;
}

function formatChannelType(channel: any): string {
  if (!channel) return 'Unknown';

  // Check for different channel types
  if (channel.channel_type) {
    const channelType = channel.channel_type;
    const types: Record<string, string> = {
      PORTAL: 'Portal',
      FAX: 'Fax',
      EMAIL: 'Email',
      MAILING_ADDRESS: 'Mail',
      WEBSITE: 'Website',
    };
    return types[channelType] || channelType;
  }

  return 'Unknown';
}
